import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader } from '../components';
import { Input } from '../components/design-system';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';

type Category = 'scanner' | 'printer' | 'camera' | 'other';
type ScannerType = 'pos' | 'a4';
type PrinterType = 'a4' | 'receipt';
type ConnectionType = 'usb' | 'network' | 'bluetooth' | 'wifi' | 'cloud';

function OptionPicker<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string; icon?: string }[];
  value: T | undefined;
  onChange: (v: T) => void;
}) {
  const styles = useThemedStyles(getStyles);
  return (
    <View style={styles.pickerGroup}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <View style={styles.optionRow}>
        {options.map((opt) => (
          <TouchableOpacity
            key={opt.value}
            style={[styles.optionChip, value === opt.value && styles.optionChipActive]}
            onPress={() => onChange(opt.value)}
            activeOpacity={0.7}
          >
            {opt.icon && (
              <Ionicons name={opt.icon as any} size={15} color={value === opt.value ? colors.white : colors.textLight} />
            )}
            <Text style={[styles.optionChipText, value === opt.value && styles.optionChipTextActive]}>
              {opt.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
}

export const DeviceCreateScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<Category | undefined>(undefined);
  const [scannerType, setScannerType] = useState<ScannerType | undefined>(undefined);
  const [printerType, setPrinterType] = useState<PrinterType | undefined>(undefined);
  const [connectionType, setConnectionType] = useState<ConnectionType | undefined>(undefined);
  const [ipAddress, setIpAddress] = useState('');
  const [port, setPort] = useState('');
  const [macAddress, setMacAddress] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [location, setLocation] = useState('');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Name is required';
    if (!category) e.category = 'Category is required';
    if (!connectionType) e.connectionType = 'Connection type is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      const res = await apiService.createDevice({
        name: name.trim(),
        description: description.trim() || undefined,
        category: category!,
        scannerType: category === 'scanner' ? scannerType : undefined,
        printerType: category === 'printer' ? printerType : undefined,
        connectionType: connectionType!,
        ipAddress: ipAddress.trim() || undefined,
        port: port ? parseInt(port) : undefined,
        macAddress: macAddress.trim() || undefined,
        serialNumber: serialNumber.trim() || undefined,
        location: location.trim() || undefined,
      });
      if (res.statusCode === 1000) {
        showNotification('success', 'Device Added', `${name} has been added`);
        navigateTo('devices');
      } else {
        showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to add device');
      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const showNetworkFields = connectionType === 'network' || connectionType === 'wifi';

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={
        <FormHeader
          title="Add Device"
          subtitle="Register a new hardware device"
          onBack={() => navigateTo('devices')}
          actions={[{ icon: 'checkmark', primary: true, onPress: handleSave, loading: saving, disabled: saving }]}
        />
      }
    >
      <View style={styles.container}>
        {/* Form */}
        <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
          {/* Basic info */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Basic Info</Text>
            <Input label="Device Name *" value={name} onChangeText={setName} placeholder="e.g. Front Desk Scanner" error={errors.name} />
            <Input label="Description" value={description} onChangeText={setDescription} placeholder="Optional notes" />
            <Input label="Location" value={location} onChangeText={setLocation} placeholder="e.g. Warehouse A, Checkout 2" />
          </View>

          {/* Device type */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Device Type</Text>
            {errors.category && <Text style={styles.fieldError}>{errors.category}</Text>}
            <OptionPicker<Category>
              label="Category"
              value={category}
              onChange={(v) => { setCategory(v); setScannerType(undefined); setPrinterType(undefined); }}
              options={[
                { value: 'scanner', label: 'Scanner', icon: 'barcode-outline' },
                { value: 'printer', label: 'Printer', icon: 'print-outline' },
                { value: 'camera', label: 'Camera', icon: 'camera-outline' },
                { value: 'other', label: 'Other', icon: 'hardware-chip-outline' },
              ]}
            />
            {category === 'scanner' && (
              <OptionPicker<ScannerType>
                label="Scanner Type"
                value={scannerType}
                onChange={setScannerType}
                options={[
                  { value: 'pos', label: 'POS / Handheld' },
                  { value: 'a4', label: 'A4 Flatbed' },
                ]}
              />
            )}
            {category === 'printer' && (
              <OptionPicker<PrinterType>
                label="Printer Type"
                value={printerType}
                onChange={setPrinterType}
                options={[
                  { value: 'a4', label: 'A4 Printer' },
                  { value: 'receipt', label: 'Receipt / Thermal' },
                ]}
              />
            )}
          </View>

          {/* Connection */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Connection</Text>
            {errors.connectionType && <Text style={styles.fieldError}>{errors.connectionType}</Text>}
            <OptionPicker<ConnectionType>
              label="Connection Type"
              value={connectionType}
              onChange={setConnectionType}
              options={[
                { value: 'usb', label: 'USB' },
                { value: 'network', label: 'Network' },
                { value: 'wifi', label: 'Wi-Fi' },
                { value: 'bluetooth', label: 'Bluetooth' },
                { value: 'cloud', label: 'Cloud' },
              ]}
            />
            {showNetworkFields && (
              <View style={styles.row}>
                <View style={styles.flex3}>
                  <Input label="IP Address" value={ipAddress} onChangeText={setIpAddress} placeholder="192.168.1.100" keyboardType="numbers-and-punctuation" />
                </View>
                <View style={styles.flex1}>
                  <Input label="Port" value={port} onChangeText={setPort} placeholder="9100" keyboardType="number-pad" />
                </View>
              </View>
            )}
            <Input label="MAC Address" value={macAddress} onChangeText={setMacAddress} placeholder="AA:BB:CC:DD:EE:FF" autoCapitalize="characters" />
            <Input label="Serial Number" value={serialNumber} onChangeText={setSerialNumber} placeholder="Optional" />
          </View>
        </ScrollView>
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  header: {
    backgroundColor: colors.background.surface,
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    ...designTokens.shadows.sm,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  backButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: typography.fontSize.xxl,
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
  },
  headerSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  saveButton: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: 20,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 64,
    ...designTokens.shadows.sm,
  },
  saveButtonDisabled: { opacity: 0.6 },
  saveButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
  },
  content: {
    padding: spacing.md,
    gap: spacing.md,
    paddingBottom: Platform.OS !== 'web' ? 100 : spacing.lg,
  },
  section: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.md,
    gap: spacing.sm,
    ...designTokens.shadows.sm,
  },
  sectionTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.textLight,
    fontFamily: typography.fontFamily.semibold,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.xs,
  },
  fieldLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
    marginBottom: spacing.xs,
  },
  pickerGroup: { gap: spacing.xs },
  optionRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  optionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 20,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  optionChipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  optionChipText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.medium,
  },
  optionChipTextActive: { color: colors.white },
  row: { flexDirection: 'row', gap: spacing.sm },
  flex3: { flex: 3 },
  flex1: { flex: 1 },
  fieldError: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
    fontFamily: typography.fontFamily.regular,
  },
});
